// Card de la observación (README §1, `.obs-detail`): la misma estructura
// que la card del propietario en la PWA y la del enlace del contratista.
//
//   [Estado] [Reabierta ×N]                       fecha
//   Título (h1 — recibe el foco al cargar la ruta)
//   Descripción breve
//   [Recinto] [Especialidad] [Criticidad]
//   ── Evidencia inicial ──
//
// Nota de nombres: `.obs-card` es la tarjeta-botón del kanban de facility
// (Observaciones.tsx) y no se toca; esta card es `.obs-detail`.

import type { RefObject } from "react";
import type { OwnerDescription } from "@bitacorapro/api-types";
import { EvidenciaInicial, type EvidenciaFoto } from "./EvidenciaInicial";
import { ObservacionPills } from "./ObservacionPills";
import type { PresentationalState } from "../lib/postventa/phase";
import type { SeverityCode } from "../lib/postventa/severity";
import { fmtDate } from "../lib/util";

export function ObservacionCard({
  titleRef,
  titulo,
  descripcion,
  fecha,
  state,
  recinto,
  especialidad,
  sev,
  sevLegacy,
  fotos,
  descripciones,
  autor,
  rol,
  avisoCritico = null,
  fotosEnConsulta = 0,
}: {
  titleRef: RefObject<HTMLHeadingElement>;
  titulo: string;
  descripcion: string | null;
  /** ISO de cuándo se reportó. */
  fecha: string;
  state: PresentationalState;
  recinto: string | null;
  especialidad: string | null;
  sev: SeverityCode | null;
  /** Clave de severidad de la receta cuando `sev` es null (ver ClasificacionPills). */
  sevLegacy?: string | null | undefined;
  fotos: readonly EvidenciaFoto[];
  descripciones: readonly OwnerDescription[];
  autor: string | null;
  rol: string;
  /** ADR-0156 — «Avisada al jefe 10:42» junto a la criticidad. */
  avisoCritico?: string | null;
  /** ADR-0163 — las fotos que quedaron de la consulta (se ven en «Desde la
   *  consulta», no como evidencia de la observación). */
  fotosEnConsulta?: number;
}) {
  return (
    <article className="obs-detail" aria-labelledby="obs-title">
      <div className="obs-detail__head">
        <ObservacionPills part="head" state={state} />
        <time className="obs-detail__date" dateTime={fecha}>
          {fmtDate(fecha)}
        </time>
      </div>
      <h1 id="obs-title" className="obs-detail__title" ref={titleRef} tabIndex={-1}>
        {titulo}
      </h1>
      {descripcion && <p className="obs-detail__desc">{descripcion}</p>}
      <div className="obs-detail__pills">
        <ObservacionPills
          part="foot"
          recinto={recinto}
          especialidad={especialidad}
          sev={sev}
          sevLegacy={sevLegacy}
        />
        {avisoCritico && <span className="cell-meta">{avisoCritico}</span>}
      </div>
      <EvidenciaInicial
        fotos={fotos}
        descripciones={descripciones}
        autor={autor}
        rol={rol}
        fotosEnConsulta={fotosEnConsulta}
      />
    </article>
  );
}
